import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOgrenciOdemeleri, addOgrenciOdeme } from '../../../services/api/ogrenciService';
import { OgrenciResponseModal, OgrenciOdemeResponseModal } from '../../../types/modals';

interface OdemeDialogModalProps {
  open: boolean;
  onClose: () => void;
  ogrenci: OgrenciResponseModal;
  sezon: number;
}

const odemeTurleri = ['Nakit', 'Kredi Kartı', 'Havale', 'EFT', 'Çek', 'Senet'];

const OdemeDialogModal: React.FC<OdemeDialogModalProps> = ({ open, onClose, ogrenci, sezon }) => {
  const queryClient = useQueryClient();
  
  // Yeni ödeme için state'ler
  const [tutar, setTutar] = useState<string>('');
  const [odemeTuru, setOdemeTuru] = useState<string>('Nakit');
  const [aciklama, setAciklama] = useState<string>('EĞİTİM');
  const [secilenSezon, setSecilenSezon] = useState<number>(sezon);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  
  // Ödemeleri getirme query'si
  const { data: odemeler, isLoading, error, refetch } = useQuery({
    queryKey: ['ogrenciOdemeleri', ogrenci.recNo],
    queryFn: async () => {
      return await getOgrenciOdemeleri({
        kurumKodu: localStorage.getItem('kurumKodu') || '',
        ogrenciId: parseInt(ogrenci.recNo)
      });
    },
    enabled: open && !!ogrenci.recNo,
    // Hata durumunda tekrar denemesini engelle
    retry: 0,
    // Hata durumunda veri önbelleğini koruma
    retryOnMount: false,
    // Hata durumunda otomatik yeniden çekmeyi engelle
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Sorgu durumunu önbellekte tut
    staleTime: Infinity
  });
  
  // Ödeme ekleme mutation'ı
  const addOdemeMutation = useMutation({
    mutationFn: (newOdeme: { tutar: number, odemeTuru: string, aciklama: string, secilenSezon: number }) => {
      return addOgrenciOdeme({
        kurumKodu: localStorage.getItem('kurumKodu') || '',
        ogrenciId: parseInt(ogrenci.recNo),
        tutar: newOdeme.tutar,
        odemeTuru: newOdeme.odemeTuru,
        aciklama: newOdeme.aciklama,
        sezon: newOdeme.secilenSezon
      });
    },
    onSuccess: () => {
      // Ödeme başarıyla eklendiğinde
      queryClient.invalidateQueries({ queryKey: ['ogrenciOdemeleri', ogrenci.recNo] });
      resetForm();
      setShowAddForm(false);
    },
    // Hata durumunda tekrar denemeyi engelle
    retry: 0,
  });
  
  // Form resetleme
  const resetForm = () => {
    setTutar('');
    setOdemeTuru('Nakit');
    setAciklama('EĞİTİM');
    setSecilenSezon(sezon);
  };
  
  // Ödeme ekleme
  const handleAddOdeme = () => {
    if (!tutar || parseFloat(tutar) <= 0) {
      alert('Lütfen geçerli bir tutar giriniz.');
      return;
    }
    
    addOdemeMutation.mutate({
      tutar: parseFloat(tutar),
      odemeTuru,
      aciklama,
      secilenSezon
    });
  };
  
  // Toplam ödeme tutarını hesaplama
  const toplamOdeme = odemeler?.reduce((total, odeme) => total + odeme.tutar, 0) || 0;
  
  // Manuel yeniden yükleme işlevi
  const handleManualRefresh = () => {
    refetch();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {ogrenci.adiSoyadi} - Ödeme Geçmişi
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Öğrenci Bilgileri */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Öğrenci No</Typography>
              <Typography variant="body1">{ogrenci.no}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Sınıf</Typography>
              <Typography variant="body1">{ogrenci.sinif}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="text.secondary">Kayıt Tarihi</Typography>
              <Typography variant="body1">{ogrenci.kayitTarihi}</Typography>
            </Grid>
          </Grid>
        </Box>
        
        {/* Ödeme Ekleme Butonu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Ödeme Geçmişi</Typography>
          <Box>
            {error && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleManualRefresh}
                sx={{ mr: 1 }}
                size="small"
              >
                Yenile
              </Button>
            )}
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? 'İptal' : 'Yeni Ödeme Ekle'}
            </Button>
          </Box>
        </Box>
        
        {/* Ödeme Ekleme Formu */}
        {showAddForm && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle1" gutterBottom>Yeni Ödeme Bilgileri</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Tutar (₺)"
                  type="number"
                  fullWidth
                  value={tutar}
                  onChange={(e) => setTutar(e.target.value)}
                  size="small"
                  InputProps={{ inputProps: { min: 0, step: "1" } }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ödeme Türü</InputLabel>
                  <Select
                    value={odemeTuru}
                    onChange={(e) => setOdemeTuru(e.target.value)}
                    label="Ödeme Türü"
                  >
                    {odemeTurleri.map((tur) => (
                      <MenuItem key={tur} value={tur}>{tur}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Açıklama"
                  fullWidth
                  value={aciklama}
                  onChange={(e) => setAciklama(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Sezon"
                  type="number"
                  fullWidth
                  value={secilenSezon}
                  onChange={(e) => setSecilenSezon(parseFloat(e.target.value))}
                  size="small"
                  InputProps={{ inputProps: { min: 2024, max: 2030, step: "1" } }}
                  required
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={resetForm}
                  disabled={addOdemeMutation.isPending}
                >
                  Temizle
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleAddOdeme}
                  disabled={!tutar || parseFloat(tutar) <= 0 || addOdemeMutation.isPending}
                >
                  {addOdemeMutation.isPending ? <CircularProgress size={24} /> : 'Ödeme Ekle'}
                </Button>
              </Grid>
            </Grid>
            
            {addOdemeMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(addOdemeMutation.error as Error).message}
              </Alert>
            )}
            
            {addOdemeMutation.isSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Ödeme başarıyla eklendi.
              </Alert>
            )}
          </Paper>
        )}
        
        {/* Ödeme Listesi */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
          >
            Öğrenci ödemesi bulunamadı. {process.env.NODE_ENV === 'development' && `(${(error as Error).message})`}
          </Alert>
        ) : !odemeler || odemeler.length === 0 ? (
          <Alert severity="info">
            Bu öğrenciye ait ödeme kaydı bulunmamaktadır.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                      Tarih
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                      Tutar (₺)
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                      Ödeme Türü
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                      Sezon
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {odemeler.map((odeme, index) => (
                    <TableRow key={index} sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                      "&:hover": { backgroundColor: "rgba(232, 137, 16, 0.1)" },
                    }}>
                      <TableCell>{odeme.tarih}</TableCell>
                      <TableCell>{odeme.tutar.toFixed(2)} ₺</TableCell>
                      <TableCell>{odeme.odemeTuru}</TableCell>
                      <TableCell>{odeme.sezon}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Toplam Tutar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Toplam: {toplamOdeme.toFixed(2)} ₺
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OdemeDialogModal;